import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { Icon, Button, Input, Loader } from "semantic-ui-react";
import { isEmpty, size } from "lodash";
import {
  useTable,
  useRowSelect,
  useSortBy,
  useFilters,
  useExpanded,
} from "react-table";
import { Pagination, SelectRowCheckBox } from "../table";
import "./PaginatedReactTable.css";

const defaultPropGetter = () => ({});

const getClassName = (isEdit, rows, className) => {
  if (!isEmpty(isEdit) && rows.length < 6)
    return `${className} react-small-table`;
  return className;
};

const ServerColumnFilter = ({ column: { filterValue, setFilter } }) => (
  <Input
    value={filterValue || ""}
    className="column-filter"
    size="mini"
    icon="filter"
    onChange={(e) => setFilter(e.target.value || undefined)}
    placeholder="Filter..."
  />
);

const ServerGlobalFilter = ({ count, value, onChange }) => (
  <Input
    className="paginated-table-search"
    size="mini"
    label="Search"
    icon="filter"
    placeholder={`Search ${count} records...`}
    value={value}
    onChange={(e, { value: v }) => onChange(v)}
  />
);

const SortIcon = ({ column }) => {
  if (!column.canSort) return null;
  if (column.isSorted) {
    return column.isSortedDesc
      ? <Icon name="sort down" className="sort-icon sort-icon-active" />
      : <Icon name="sort up" className="sort-icon sort-icon-active" />;
  }
  return <Icon name="sort" className="sort-icon sort-icon-inactive" />;
};

const createHeaderGroup = (headerGroups) =>
  headerGroups.map((headerGroup) => {
    const { key, ...groupProps } = headerGroup.getHeaderGroupProps();
    return (
      <tr key={key} {...groupProps}>
        {headerGroup.headers.map((column) => {
          const columnHeaderProps = column.getHeaderProps();
          const columnHeaderKey = columnHeaderProps.key;
          delete columnHeaderProps.key;

          let sortToggleProps = {};
          if (column.canSort) {
            const raw = column.getSortByToggleProps();
            delete raw.key;
            sortToggleProps = raw;
          }

          const thClassName = [
            column.canSort ? "sortable-header" : "",
            column.isSorted ? "sorted-header" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <th
              key={columnHeaderKey}
              {...columnHeaderProps}
              {...sortToggleProps}
              className={thClassName || undefined}
            >
              <div className="header-label">
                <span>{column.render("Header")}</span>
                <SortIcon column={column} />
              </div>
              {column.canFilter && (
                <div
                  className="header-filter"
                  onClick={(e) => e.stopPropagation()}
                >
                  {column.render("Filter")}
                </div>
              )}
            </th>
          );
        })}
      </tr>
    );
  });

export const PaginatedReactTable = ({
  className = "",
  columns,
  data,
  hiddenColumns = [],
  editable = false,
  renderChildRow,
  updateMyData,
  setOptions,
  options,
  skipReset,
  setEdit,
  isEdit = {},
  editSingleRow = false,
  hideGlobalFilter = false,
  showFooter = false,
  onSelectionChange,
  displayErrors = defaultPropGetter,
  getRowProps = defaultPropGetter,
  exportName = "",
  additionalData = "",
  additionalKey = "",
  includeExportColsOnly = false,
  exportCols = [],
  // Server-side props
  count = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onFilterChange,
  loading = false,
  // Initial state from URL params
  initialFilters = {},
  initialOrdering = "",
}) => {
  const [searchValue, setSearchValue] = useState(initialFilters?.search || "");

  const defaultColumn = useMemo(() => ({ Filter: ServerColumnFilter }), []);

  const pageCount = Math.ceil(count / pageSize) || 1;

  // Compute initial react-table filter state from server-side filter params (URL on mount)
  const initialFiltersForTable = useMemo(() => {
    return Object.entries(initialFilters)
      .filter(([k, v]) => k !== "search" && v)
      .map(([key, value]) => {
        const col =
          columns.find((c) => c.serverFilterParam === key) ||
          columns.find((c) => c.accessor === key || c.id === key);
        return col ? { id: col.accessor || col.id, value } : null;
      })
      .filter(Boolean);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute initial sort state from ordering URL param (e.g. "-iccName" → [{ id: "iccName", desc: true }])
  const initialSortBy = useMemo(() => {
    if (!initialOrdering) return [];
    const desc = initialOrdering.startsWith("-");
    const orderingKey = desc ? initialOrdering.slice(1) : initialOrdering;
    const col =
      columns.find((c) => c.serverSortParam === orderingKey) ||
      columns.find((c) => c.accessor === orderingKey || c.id === orderingKey);
    const id = col ? col.accessor || col.id : orderingKey;
    return [{ id, desc }];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const instance = useTable(
    {
      columns,
      data,
      defaultColumn,
      updateMyData,
      setOptions,
      options,
      manualSortBy: true,
      manualFilters: true,
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetSelectedRows: !skipReset,
      initialState: { hiddenColumns, filters: initialFiltersForTable, sortBy: initialSortBy },
    },
    useFilters,
    useSortBy,
    useExpanded,
    useRowSelect,
    (hooks) => {
      editable &&
        hooks.visibleColumns.push((cols) => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                {!editSingleRow && (
                  <SelectRowCheckBox
                    setEdit={setEdit}
                    row="all"
                    isEdit={isEdit}
                    {...getToggleAllRowsSelectedProps()}
                  />
                )}
              </div>
            ),
            Cell: ({ row }) => (
              <div>
                <SelectRowCheckBox
                  setEdit={setEdit}
                  row={row}
                  editSingleRow={editSingleRow}
                  isEdit={isEdit}
                  {...row.getToggleRowSelectedProps()}
                />
              </div>
            ),
          },
          ...cols,
        ]);
    }
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    visibleColumns,
    setAllFilters,
    state: { sortBy, filters, selectedRowIds },
  } = instance;

  // Notify parent of row selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedRowIds);
    }
  }, [selectedRowIds, onSelectionChange]);

  // Sync react-table sort state → onSortChange (skip initial mount)
  const sortMountedRef = useRef(false);
  useEffect(() => {
    if (!sortMountedRef.current) {
      sortMountedRef.current = true;
      return;
    }
    if (!onSortChange) return;
    if (sortBy.length === 0) {
      onSortChange("");
    } else {
      const { id, desc } = sortBy[0];
      const col = columns.find((c) => c.accessor === id || c.id === id);
      const serverKey = col?.serverSortParam || id;
      onSortChange(desc ? `-${serverKey}` : serverKey);
    }
  }, [sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync react-table filter state → onFilterChange (skip initial mount)
  const filterMountedRef = useRef(false);
  const prevFiltersRef = useRef([]);
  useEffect(() => {
    if (!filterMountedRef.current) {
      filterMountedRef.current = true;
      prevFiltersRef.current = filters;
      return;
    }
    if (!onFilterChange) return;

    const prev = prevFiltersRef.current;

    // Changed or newly set filters
    filters.forEach(({ id, value }) => {
      const prevFilter = prev.find((f) => f.id === id);
      if (!prevFilter || prevFilter.value !== value) {
        const col = columns.find((c) => c.accessor === id || c.id === id);
        const key = col?.serverFilterParam || id;
        onFilterChange(key, value ?? "");
      }
    });

    // Cleared filters (present in prev but not current)
    prev.forEach(({ id }) => {
      if (!filters.find((f) => f.id === id)) {
        const col = columns.find((c) => c.accessor === id || c.id === id);
        const key = col?.serverFilterParam || id;
        onFilterChange(key, "");
      }
    });

    prevFiltersRef.current = filters;
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResetFilter = useCallback(() => {
    setAllFilters([]);
    setSearchValue("");
    if (onFilterChange) onFilterChange("search", "");
  }, [setAllFilters, onFilterChange]);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchValue(value);
      if (onFilterChange) onFilterChange("search", value);
    },
    [onFilterChange]
  );

  const paginationProps = {
    gotoPage: (idx) => onPageChange && onPageChange(idx + 1),
    canPreviousPage: page > 1,
    canNextPage: page < pageCount,
    previousPage: () => onPageChange && onPageChange(page - 1),
    nextPage: () => onPageChange && onPageChange(page + 1),
    pageIndex: page - 1,
    pageOptions: Array.from({ length: pageCount }, (_, i) => i),
    pageCount,
    pageSize,
    setPageSize: (newSize) => onPageSizeChange && onPageSizeChange(newSize),
  };

  return (
    <div className="mobile-friendly-table-wrapper paginated-react-table">
      {!hideGlobalFilter && (
        <div className="paginated-table-toolbar">
          <ServerGlobalFilter
            count={count}
            value={searchValue}
            onChange={handleSearchChange}
          />
          <Button onClick={handleResetFilter}>Clear Filters</Button>
        </div>
      )}
      <div className="mobile-friendly-table-div-wrapper">
        <div className="mobile-friendly-table-div" aria-busy={loading}>
          {loading && (
            <div className="paginated-table-loading-overlay">
              <Loader active>Loading...</Loader>
            </div>
          )}
          <table
            id="react-table"
            className={getClassName(isEdit, rows, className)}
            {...getTableProps()}
          >
            <thead>{createHeaderGroup(headerGroups)}</thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row, i) => {
                prepareRow(row);
                const rowProps = row.getRowProps(getRowProps(row));
                const rowKey = rowProps.key;
                delete rowProps.key;
                return (
                  <React.Fragment key={i}>
                    <tr key={rowKey} {...rowProps}>
                      {row.cells.map((cell) => {
                        const cellProps = cell.getCellProps();
                        const cellKey = cellProps.key;
                        delete cellProps.key;
                        return (
                          <td key={cellKey} {...cellProps}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                    {row.isExpanded ? (
                      <tr key={i}>
                        <td key={i + "cell"} colSpan={visibleColumns.length}>
                          {renderChildRow({ row })}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
            {showFooter && (
              <tfoot>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <td {...column.getHeaderProps()}>
                        {column.render("Header")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
        <div className="row-counts">
          <span>Total Rows: {count}</span>
          {editable && (
            <span className="row-count-span">
              Rows Selected:{" "}
              {editSingleRow && !isEmpty(selectedRowIds)
                ? 1
                : size(selectedRowIds)}
            </span>
          )}
        </div>
        <Pagination {...paginationProps} />
      </div>
    </div>
  );
};
